<template>
    <div class="news-community-carousel">
        <div class="news-community-carousel__header flex flex-wrap justify-between items-center gap-4 mb-[40px]">
            <h2 v-if="title" class="font-normal text-left m-0 flex-shrink min-w-0">{{ title }}</h2>
            <div class="button-stack flex flex-wrap gap-4">
                <slot name="buttons"></slot>
            </div>
        </div>

        <div class="relative">
            <div class="news-community-carousel-grid" :class="transitionClass"
                 @touchstart="handleTouchStart"
                 @touchmove="handleTouchMove"
                 @touchend="handleTouchEnd"
                 :style="touchStyle"
            >
                <div class="news-card-holder" v-for="(item, index) in allItems" :key="index">
                    <a v-if="item.LinkURL" :href="item.LinkURL" :target="newWindow(item)"
                         class="news-card cursor-pointer no-underline block">
                        <picture class="news-card__image-wrapper block overflow-hidden">
                            <img :src="item.ImageURL"
                                 :alt="item.Title"
                                 class="news-card__image w-full h-full object-cover"
                                 :style="{
                                    objectPosition: item.FocusPointX + '% ' + item.FocusPointY + '%'
                                 }" />
                        </picture>
                        <div class="news-card__content">
                            <h3 class="news-card__title font-normal mt-[20px] mb-[10px]">{{ item.Title }}</h3>
                            <p v-if="item.Subtitle" class="news-card__subtitle m-0">{{ item.Subtitle }}</p>
                        </div>
                    </a>
                    <div v-else class="news-card block">
                        <picture class="news-card__image-wrapper block overflow-hidden m-0">
                            <img :src="item.ImageURL"
                                 :alt="item.Title"
                                 class="news-card__image w-full h-full object-cover"
                                 :style="{
                                    objectPosition: item.FocusPointX + '% ' + item.FocusPointY + '%'
                                 }" />
                        </picture>
                        <div class="news-card__content">
                            <h3 class="news-card__title font-normal mt-[20px] mb-[10px]">{{ item.Title }}</h3>
                            <p v-if="item.Subtitle" class="news-card__subtitle m-0">{{ item.Subtitle }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Navigation buttons -->
            <div class="news-community-controls">
                <button class="p-0 btn-main w-[60px] md:w-[80px] h-[60px] md:h-[80px] rounded-full flex flex-center-all news-community-btn--left" v-on:click.prevent="shiftRight">
                    <svg class="carousel__icon" xmlns="http://www.w3.org/2000/svg" width="80"
                         height="80" viewBox="0 0 80 80">
                        <g data-name="Group 524" transform="rotate(180 607.124 2216.624)">
                            <rect data-name="Rectangle 23" width="80" height="80" rx="40"
                                  transform="translate(1134.248 4353.248)" style="fill: none"/>
                            <g data-name="Group 47">
                                <path data-name="Line 58" transform="rotate(-90 2774.319 1619.259)"
                                      style="stroke-linejoin:round;fill:none;stroke-width:2px"
                                      d="M0 0v37.709"/>
                                <path data-name="Path 36" d="m0 0 9.252 9.252L18.5 0"
                                      transform="rotate(-90 2793.27 1609.56)"
                                      style="fill:none;stroke-width:2px"/>
                            </g>
                        </g>
                    </svg><span class="sr-only">Previous</span>
                </button>
                <button class="p-0 btn-main w-[60px] md:w-[80px] h-[60px] md:h-[80px] rounded-full flex flex-center-all news-community-btn--right" v-on:click.prevent="shiftLeft">
                    <svg class="carousel__icon" xmlns="http://www.w3.org/2000/svg" width="80" height="80"
                         viewBox="0 0 80 80">
                        <g data-name="Group 523" transform="translate(-1134.306 -4353.248)">
                            <rect data-name="Rectangle 23" width="80" height="80" rx="40"
                                  transform="translate(1134.306 4353.248)" style="fill: none"/>
                            <g data-name="Group 47">
                                <path data-name="Line 58" transform="rotate(-90 2774.341 1619.266)"
                                      style="stroke-linejoin:round;fill:none;stroke-width:2px"
                                      d="M0 0v37.736"/>
                                <path data-name="Path 36" d="m0 0 9.258 9.258L18.517 0"
                                      transform="rotate(-90 2793.306 1609.56)"
                                      style="fill:none;stroke-width:2px"/>
                            </g>
                        </g>
                    </svg><span class="sr-only">Next</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "NewsAndCommunity",
    data() {
        return {
            allItems: [],
            shift: 0,
            isTransitioning: false,
            transitionClass: '',
            touchStartX: 0,
            touchEndX: 0,
            minSwipeDistance: 50,
            isTouching: false,
            touchStyle: {}
        };
    },
    props: {
        items: {
            type: String,
            default: '[]'
        },
        title: {
            type: String,
            default: ""
        }
    },
    mounted() {
        this.allItems = JSON.parse(this.items);
        const carouselGrid = this.$el.querySelector('.news-community-carousel-grid');
        if (carouselGrid) {
            carouselGrid.addEventListener('touchmove', this.handleTouchMove, {passive: false});
        }
    },
    beforeUnmount() {
        const carouselGrid = this.$el.querySelector('.news-community-carousel-grid');
        if (carouselGrid) {
            carouselGrid.removeEventListener('touchmove', this.handleTouchMove);
        }
    },
    methods: {
        shiftLeft: function () {
            this.isTransitioning = true;
            this.transitionClass = 'transition-left';
            this.touchStyle = {};

            setTimeout(() => {
                const firstItem = this.allItems.shift();
                this.allItems.push(firstItem);

                setTimeout(() => {
                    this.isTransitioning = false;
                    this.transitionClass = '';
                }, 50);
            }, 100);
        },
        shiftRight: function () {
            this.isTransitioning = true;
            this.transitionClass = 'transition-right';
            this.touchStyle = {};

            setTimeout(() => {
                const lastItem = this.allItems.pop();
                this.allItems.unshift(lastItem);

                setTimeout(() => {
                    this.isTransitioning = false;
                    this.transitionClass = '';
                }, 50);
            }, 100);
        },
        newWindow: function (item) {
            if (item.OpenInNewWindow === 'true') {
                return "_blank";
            }
            return "_self";
        },
        handleTouchStart(e) {
            if (this.isTransitioning) return;

            this.touchStartX = e.changedTouches[0].screenX;
            this.isTouching = true;
            this.touchDeltaX = 0;
            this.transitionClass = '';
        },
        handleTouchMove(e) {
            if (!this.isTouching || this.isTransitioning) return;

            this.touchEndX = e.changedTouches[0].screenX;
            this.touchDeltaX = this.touchEndX - this.touchStartX;

            const resistanceFactor = 0.5;
            const transformX = this.touchDeltaX * resistanceFactor;

            this.touchStyle = {
                transform: `translateX(${transformX}px)`,
                transition: 'none'
            };

            if (Math.abs(this.touchDeltaX) > 10 &&
                Math.abs(this.touchDeltaX) > Math.abs(e.changedTouches[0].screenY - e.targetTouches[0].screenY)) {
                e.preventDefault();
            }
        },
        handleTouchEnd(e) {
            if (!this.isTouching || this.isTransitioning) return;
            this.isTouching = false;

            this.touchEndX = e.changedTouches[0].screenX;
            this.touchDeltaX = this.touchEndX - this.touchStartX;

            this.touchStyle = {
                transform: 'translateX(0)',
                transition: 'transform 0.3s ease'
            };

            if (Math.abs(this.touchDeltaX) >= this.minSwipeDistance) {
                if (this.touchDeltaX > 0) {
                    this.shiftRight();
                } else {
                    this.shiftLeft();
                }
            }

            setTimeout(() => {
                this.touchStyle = {};
            }, 300);
        }
    }
}
</script>

<style scoped>
.news-community-carousel-grid {
    transition: transform 0.3s ease;
    will-change: transform;
    touch-action: pan-y;
}

.news-card {
    transition: opacity 300ms ease;
}

.transition-left .news-card-holder {
    animation: slideLeft 100ms ease-in-out;
}

.transition-right .news-card-holder {
    animation: slideRight 100ms ease-in-out;
}

@keyframes slideLeft {
    0% {
        transform: translateX(0);
    }
    100% {
        transform: translateX(-100%);
    }
}

@keyframes slideRight {
    0% {
        transform: translateX(0);
    }
    100% {
        transform: translateX(100%);
    }
}
</style>
